import { addDisplayName, addProps, addState, flowMax } from 'ad-hok'
import { branchIfFailsPredicate } from 'ad-hok-utils'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import cx from 'classnames'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { BrandInput, BrandProductStats } from 'shared/src/api/types'
import {
  addDataLoadingFromPromise,
  hasData,
} from 'shared/src/utils/dataLoading'
import { typedAs } from 'shared/src/utils/typedAs'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { makeAdminProductRoute } from 'utils/routes'
import { DateRange, EARLIEST_DATE, getToday } from './DateRange'
import { COLUMN_TYPES, DEFAULT_COL_DEF } from './ResourceIndex'
import { TextField } from './TextField'
import { FormProps } from './resource'
import { SECONDARY_TITLE_STYLE } from './styles'

const useStyles = createUseStyles({
  productsTitle: SECONDARY_TITLE_STYLE,
  table: {
    marginTop: 20,
    width: '100%',
    height: 400,
  },
  matchesRenderer: {
    display: 'flex',
    gap: 5,
  },
  totals: {
    marginTop: 20,
  },
})

const sumOfField = <
  TField extends string,
  TObject extends { [field in TField]: number }
>(
  field: TField,
  objects: TObject[]
) => objects.reduce((acc, object) => acc + object[field], 0)

const IdCellRenderer: FC<
  ICellRendererParams<undefined, BrandProductStats['id']>
> = flowMax(
  addDisplayName('IdCellRenderer'),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ value: id, classes }) => (
    <div className={classes.matchesRenderer}>
      <a href={makeAdminProductRoute(id)} target="_blank" rel="noreferrer">
        {id}
      </a>
    </div>
  )
)

const MatchesCellRenderer: FC<
  ICellRendererParams<undefined, BrandProductStats['matches']>
> = flowMax(
  addDisplayName('MatchesCellRenderer'),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ value: ids, classes }) => (
    <div className={classes.matchesRenderer}>
      {ids.map((id) => (
        <a href={makeAdminProductRoute(id)} target="_blank" rel="noreferrer">
          {id}
        </a>
      ))}
    </div>
  )
)

interface ProductsProps {
  id: number
}

const Products: FC<ProductsProps> = flowMax(
  addDisplayName('Products'),
  addState('dateRange', 'setDateRange', [EARLIEST_DATE, getToday()]),
  addApi,
  addDataLoadingFromPromise(
    'stats',
    ({ id, dateRange: [startDate, endDate], api }) =>
      api.brands.productStats(id, startDate, endDate, true),
    ['id', 'dateRange']
  ),
  branchIfFailsPredicate('stats', hasData),
  addTranslationHelpers,
  addProps(({ stats }) => ({
    totals: {
      saves: sumOfField('saves', stats.data),
      views: sumOfField('views', stats.data),
      outgoingClicks: sumOfField('outgoingClicks', stats.data),
    },
  })),
  addProps(({ t }) => ({
    columnDefs: typedAs<ColDef<BrandProductStats>[]>([
      {
        headerName: t('admin.resources.general.columns.id'),
        field: 'id',
        cellRenderer: IdCellRenderer,
      },
      {
        headerName: t('admin.resources.products.fields.name'),
        field: 'name',
      },
      {
        headerName: t('admin.resources.products.fields.saves'),
        field: 'saves',
        type: ['number'],
      },
      {
        headerName: t('admin.resources.products.fields.views'),
        field: 'views',
        type: ['number'],
      },
      {
        headerName: t('admin.resources.products.fields.outbounds'),
        field: 'outgoingClicks',
        type: ['number'],
      },
      {
        headerName: t('admin.resources.brands.productStatsFields.matches'),
        field: 'matches',
        cellRenderer: MatchesCellRenderer,
      },
    ]),
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    dateRange: [startDate, endDate],
    setDateRange,
    stats,
    columnDefs,
    totals,
    classes,
    t,
  }) => (
    <div>
      <span className={classes.productsTitle}>
        {t('admin.resources.brands.products')}
      </span>
      <DateRange
        startDate={startDate}
        endDate={endDate}
        onChange={(startDate, endDate) => {
          setDateRange([startDate, endDate])
        }}
      />
      <div className={cx('ag-theme-alpine', classes.table)}>
        <AgGridReact
          columnDefs={columnDefs}
          columnTypes={COLUMN_TYPES}
          defaultColDef={DEFAULT_COL_DEF}
          rowData={stats.data}
        />
      </div>
      <div className={classes.totals}>
        <ul>
          <li>
            {t('admin.resources.products.fields.saves')} {totals.saves}
          </li>
          <li>
            {t('admin.resources.products.fields.views')} {totals.views}
          </li>
          <li>
            {t('admin.resources.products.fields.outbounds')}{' '}
            {totals.outgoingClicks}
          </li>
        </ul>
      </div>
    </div>
  )
)

export const BrandsForm: FC<FormProps<Partial<BrandInput>>> = flowMax(
  addDisplayName('BrandsForm'),
  addTranslationHelpers,
  ({ id, values, onChange, errors, t }) => (
    <div>
      <TextField
        id="name"
        label={t('admin.resources.brands.fields.name')}
        value={values.name ?? ''}
        onChange={(name) => onChange({ ...values, name })}
        error={errors.name}
        focusOnMount
      />
      <TextField
        id="domain"
        label={t('admin.resources.brands.fields.domain')}
        value={values.domain ?? ''}
        onChange={(domain) => onChange({ ...values, domain })}
        error={errors.domain}
        focusOnMount
      />
      {id && <Products id={id} />}
    </div>
  )
)
